var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/lib/index.ts
import { Prisma } from "@prisma/client";

// src/rpc/index.ts
function createRPC(...[type, options]) {
  const remoteCall = type === "apiConsumer" ? options.remoteCall : void 0;
  const securityOptions = options.securityOptions || {};
  const forbiddenProps = /* @__PURE__ */ new Set([
    "__proto__",
    "constructor",
    "prototype",
    "__defineGetter__",
    "__defineSetter__",
    "__lookupGetter__",
    "__lookupSetter__",
    ...securityOptions.forbiddenProps || []
  ]);
  const isAllowedPath = (method) => {
    if (!securityOptions.allowedPaths || securityOptions.allowedPaths.length === 0) {
      return true;
    }
    return securityOptions.allowedPaths.some((allowedPath) => {
      if (allowedPath.endsWith(".*")) {
        const prefix = allowedPath.slice(0, -2);
        return method === prefix || method.startsWith(prefix + ".");
      }
      return method === allowedPath;
    });
  };
  async function RC(method, data) {
    if (!isAllowedPath(method)) {
      throw new Error(`\u65B9\u6CD5 ${method} \u4E0D\u5728\u5141\u8BB8\u7684\u8DEF\u5F84\u767D\u540D\u5355\u4E2D`);
    }
    async function executeMiddleware(index) {
      if (options.middleware && index < options.middleware.length) {
        return options.middleware[index]?.(method, data, () => executeMiddleware(index + 1));
      } else {
        return executeCall();
      }
    }
    async function executeCall() {
      try {
        if (type === "apiProvider") {
          const apiModule = await options.genApiModule();
          const methodParts = method.split(".");
          for (const part of methodParts) {
            if (forbiddenProps.has(part)) {
              throw new Error(`\u7981\u6B62\u8BBF\u95EE\u654F\u611F\u5C5E\u6027: ${part}`);
            }
          }
          let currentObj = apiModule;
          for (const part of methodParts) {
            currentObj = currentObj[part];
            if (!currentObj) {
              throw new Error(`\u65B9\u6CD5 ${method} \u672A\u627E\u5230`);
            }
          }
          if (typeof currentObj === "function") {
            if (currentObj.toString().includes("[native code]")) {
              throw new Error(`\u7981\u6B62\u8C03\u7528\u539F\u751F\u65B9\u6CD5`);
            }
            return await currentObj(...data);
          } else {
            throw new Error(`${method} \u4E0D\u662F\u4E00\u4E2A\u51FD\u6570`);
          }
        } else {
          return await remoteCall(method, data);
        }
      } catch (error) {
        throw error;
      }
    }
    return await executeMiddleware(0);
  }
  function createNestedProxy(path = []) {
    return {
      get(_target, prop) {
        if (typeof prop === "string" && forbiddenProps.has(prop)) {
          throw new Error(`\u7981\u6B62\u8BBF\u95EE\u654F\u611F\u5C5E\u6027: ${prop}`);
        }
        if (prop === "then") {
          return void 0;
        }
        const newPath = [...path, prop];
        const fullPath = newPath.join(".");
        if (!isAllowedPath(fullPath)) {
          throw new Error(`\u8DEF\u5F84 ${fullPath} \u4E0D\u5728\u5141\u8BB8\u7684\u767D\u540D\u5355\u4E2D`);
        }
        const proxyObj = new Proxy((...args) => {
          const method = newPath.join(".");
          return RC(method, args);
        }, createNestedProxy(newPath));
        return proxyObj;
      },
      apply(_target, _thisArg, args) {
        const method = path.join(".");
        return RC(method, args);
      }
    };
  }
  const API = new Proxy(() => {
  }, createNestedProxy());
  return { API, RC };
}
function proxyCall(_obj, transform) {
  let path = [];
  const createProxy = () => {
    return new Proxy(function() {
    }, {
      // 拦截函数调用
      apply(_target, _thisArg, args) {
        const result = [path.join("."), args];
        path = [];
        return transform ? transform(result) : result;
      },
      // 拦截属性访问
      get(_target, prop, _receiver) {
        if (typeof prop === "string" && prop !== "then") {
          path.push(prop);
          return createProxy();
        }
        return void 0;
      }
    });
  };
  const handler = {
    get(_target, prop, _receiver) {
      if (typeof prop === "string" && prop !== "then") {
        path.push(prop);
        return createProxy();
      }
      return void 0;
    }
  };
  return new Proxy({}, handler);
}

// src/lib/SessionAuthSign.ts
var SessionAuthSign_exports = {};
__export(SessionAuthSign_exports, {
  signByToken: () => signByToken,
  verifySignByToken: () => verifySignByToken
});
import CryptoJS from "crypto-js";
function cryptoJsHmacSha256(key, data) {
  const hmac = CryptoJS.HmacSHA256(data, key);
  return CryptoJS.enc.Hex.stringify(hmac);
}
function hexToBytes(hex) {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
  }
  return bytes;
}
function timingSafeEqual(a, b) {
  if (a.length !== b.length) {
    return false;
  }
  const aBytes = hexToBytes(a);
  const bBytes = hexToBytes(b);
  if (aBytes.length !== bBytes.length) {
    return false;
  }
  let result = 0;
  for (let i = 0; i < aBytes.length; i++) {
    result |= aBytes[i] ^ bBytes[i];
  }
  return result === 0;
}
async function signByToken(originStr, sessionToken) {
  return cryptoJsHmacSha256(sessionToken, originStr);
}
async function verifySignByToken(originStr, sessionToken, providedSignature) {
  try {
    const expectedSignature = await signByToken(originStr, sessionToken);
    return timingSafeEqual(expectedSignature, providedSignature);
  } catch (error) {
    return false;
  }
}
export {
  Prisma,
  SessionAuthSign_exports as SessionAuthSign,
  createRPC,
  proxyCall
};
//# sourceMappingURL=index.js.map